import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Trash2, Mail, X, Copy, Check, Loader2, Pencil, Download, Upload, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["À contacter", "Contacté", "Relancé", "Réponse reçue", "Booké", "Sans suite"];
const TYPES = ["Mairie", "Comité des fêtes", "Office de tourisme", "Agglomération", "Bar / Salle de concert", "Salle / centre culturel", "Département", "Autre"];

const STATUS_STYLE = {
  "À contacter": "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  "Contacté": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "Relancé": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  "Réponse reçue": "bg-sky-500/15 text-sky-400 border-sky-500/30",
  "Booké": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "Sans suite": "bg-red-500/15 text-red-400 border-red-500/30",
};

const EMPTY = {
  nom: "",
  type: "Mairie",
  departement: "",
  email: "",
  telephone: "",
  site_web: "",
  statut: "À contacter",
  notes: "",
  dernier_contact: "",
};

const inputCls =
  "w-full bg-[#09090D] border border-zinc-800 focus:border-amber-500/60 focus:outline-none rounded-xl px-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300";

const BAR_TYPES = ["Bar / Salle de concert", "Salle / centre culturel"];

const buildEmailDraft = (p) => {
  if (BAR_TYPES.includes(p.type)) {
    return {
      subject: "Soirée concert rock dans votre établissement — Forty One",
      body: `Bonjour,

Forty One est un groupe de reprises rock : une chanteuse à la voix exceptionnelle et quatre musiciens (guitare, basse, claviers/piano, batterie). Notre répertoire rassemble les grands classiques du rock, des années 70 à aujourd'hui (Queen, Toto, The Cranberries, Gossip…) — de quoi créer une vraie ambiance et fidéliser votre clientèle.

Nous serions ravis de jouer chez vous (${p.nom || "votre établissement"}) : set 100% live de 2 × 60 minutes, format adaptable — concert assis, debout, en happy hour ou en soirée. Nous apportons nos instruments et nos amplis ; pour les petites jauges notre matériel suffit, pour les plus grandes salles une sono est préférable.

Côté cachet, nous nous adaptons à votre jauge et à votre format : parlons-en simplement.

Vous pouvez nous écouter sur notre chaîne YouTube : https://www.youtube.com/@41fortyone

Seriez-vous partant pour programmer une date ce semestre ?

Musicalement,
Forty One
fortyone@forty-one.fr — 06 60 91 67 64`,
    };
  }
  return {
    subject: `Concert rock pour vos événements — Forty One`,
    body: `Madame, Monsieur,

Forty One est un groupe de reprises rock composé de cinq artistes : une chanteuse à la voix exceptionnelle, un guitariste, un bassiste, un claviériste et un batteur. Nous reprenons les grands classiques du rock, des années 70 à aujourd'hui (Toto, The Cranberries, Gossip, Céline Dion & Jean-Jacques Goldman…).

Nous serions ravis de nous produire lors de vos événements à ${p.nom || "votre collectivité"}${p.departement ? ` (${p.departement})` : ""} : fête locale, festival municipal, concert estival, 14 juillet ou fête de la musique.

Notre formule est simple : un concert 100% live de 2 × 60 minutes, un plan de scène et une fiche technique fournis, et un répertoire qui rassemble toutes les générations.

Vous pouvez découvrir le groupe en vidéo sur notre chaîne YouTube : https://www.youtube.com/@41fortyone

Seriez-vous disponible pour en échanger ? Nous nous adaptons à vos contraintes de date, de lieu et de budget.

Musicalement,
Forty One
fortyone@forty-one.fr`,
  };
};

export default function ProspectsTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', data}
  const [draft, setDraft] = useState(null); // prospect for email modal
  const [copied, setCopied] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/prospects");
      setItems(data);
    } catch (err) {
      toast.error("Chargement impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(() => {
    if (!items) return [];
    return items.filter((p) => {
      const okStatus = filter === "Tous" || p.statut === filter;
      const q = search.trim().toLowerCase();
      const okSearch = !q || [p.nom, p.departement, p.type, p.email].join(" ").toLowerCase().includes(q);
      return okStatus && okSearch;
    });
  }, [items, filter, search]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal.mode === "create") {
        const { data } = await api.post("/prospects", modal.data);
        setItems((list) => [data, ...list]);
        toast.success("Collectivité ajoutée");
      } else {
        const { data } = await api.patch(`/prospects/${modal.data.id}`, modal.data);
        setItems((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success("Fiche mise à jour");
      }
      setModal(null);
    } catch (err) {
      toast.error("Enregistrement impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const patchStatus = async (p, statut) => {
    try {
      const { data } = await api.patch(`/prospects/${p.id}`, { ...p, statut });
      setItems((list) => list.map((it) => (it.id === p.id ? data : it)));
      toast.success(`Statut : ${statut}`);
    } catch (err) {
      toast.error("Échec de la mise à jour", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/prospects/${id}`);
      setItems((list) => list.filter((p) => p.id !== id));
      toast.success("Collectivité supprimée");
    } catch (err) {
      toast.error("Suppression impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const activeDraft = draft
    ? aiDraft && aiDraft.for === draft.id
      ? aiDraft
      : buildEmailDraft(draft)
    : null;

  const copyDraft = async () => {
    if (!activeDraft) return;
    try {
      await navigator.clipboard.writeText(`Objet : ${activeDraft.subject}\n\n${activeDraft.body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Email copié dans le presse-papiers");
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  const generateAi = async () => {
    if (!draft) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/prospects/${draft.id}/email-ia`);
      setAiDraft({ ...data, for: draft.id });
      toast.success("Email personnalisé généré par l'IA");
    } catch (err) {
      toast.error("Génération IA impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setAiLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!draft) return;
    const version = aiDraft && aiDraft.for === draft.id ? "ia" : "standard";
    if (
      !window.confirm(
        `Envoyer l'email (${version === "ia" ? "version IA" : "modèle standard"}) à ${draft.email} ?`
      )
    )
      return;
    setSendLoading(true);
    try {
      const { data } = await api.post(`/prospects/${draft.id}/send-email`, { mode: version });
      setItems((list) => list.map((p) => (p.id === data.id ? data : p)));
      toast.success(`Email envoyé à ${data.email}`, {
        description: "Fiche technique incluse en lien de téléchargement. Statut passé à « Contacté ».",
      });
      setDraft(null);
      setAiDraft(null);
    } catch (err) {
      toast.error("Envoi impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSendLoading(false);
    }
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const text = await importFile.text();
      const { data } = await api.post("/prospects/import", text, {
        headers: { "Content-Type": "text/csv; charset=utf-8" },
      });
      toast.success(`${data.added} collectivité(s) importée(s)`, {
        description: data.skipped ? `${data.skipped} ignorée(s) — doublon ou ligne sans nom` : undefined,
      });
      setImportOpen(false);
      setImportFile(null);
      load();
    } catch (err) {
      toast.error("Import impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setImporting(false);
    }
  };

  const counts = useMemo(() => {
    const c = { Tous: items?.length || 0 };
    STATUSES.forEach((s) => (c[s] = 0));
    (items || []).forEach((p) => (c[p.statut] = (c[p.statut] || 0) + 1));
    return c;
  }, [items]);

  if (items === null) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-amber-500" size={28} data-testid="prospects-loading" />
      </div>
    );
  }

  return (
    <div data-testid="prospection-crm">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="prospect-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une collectivité…"
            className={`${inputCls} pl-9 w-64`}
          />
        </div>
        <select
          data-testid="prospect-status-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-[#09090D] border border-zinc-800 focus:border-amber-500/60 focus:outline-none rounded-xl px-4 py-2.5 text-sm text-zinc-200"
        >
          {["Tous", ...STATUSES].map((s) => (
            <option key={s} value={s}>
              {s} ({counts[s] || 0})
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="import-csv-button"
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-2 border border-zinc-700 hover:border-amber-500/40 text-zinc-300 hover:text-amber-400 text-sm font-bold uppercase tracking-wider rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            <Upload size={15} />
            Import CSV
          </button>
          <button
            data-testid="export-csv-button"
            onClick={() => window.open("/api/prospects/export.csv", "_blank")}
            className="flex items-center gap-2 border border-zinc-700 hover:border-amber-500/40 text-zinc-300 hover:text-amber-400 text-sm font-bold uppercase tracking-wider rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            <Download size={15} />
            Export CSV
          </button>
          <button
            data-testid="add-prospect-button"
            onClick={() => setModal({ mode: "create", data: { ...EMPTY } })}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold uppercase tracking-wider rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            <Plus size={15} />
            Ajouter
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div data-testid="prospects-empty-state" className="rounded-2xl border border-dashed border-zinc-800 py-20 text-center">
          <p className="text-zinc-400 font-semibold">Aucune collectivité trouvée</p>
          <p className="text-sm text-zinc-600 mt-1">Modifiez vos filtres ou ajoutez une nouvelle collectivité.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-800">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="bg-[#161622] text-left">
                {["Collectivité", "Type", "Département", "Statut", "Dernier contact", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-jbmono uppercase tracking-widest text-zinc-500 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} data-testid={`prospect-row-${p.id}`} className="border-t border-zinc-800/70 hover:bg-[#161622]/60 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-zinc-100">{p.nom}</p>
                    {p.email && <p className="text-xs text-zinc-500">{p.email}</p>}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{p.type}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{p.departement || "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`prospect-status-select-${p.id}`}
                      value={p.statut}
                      onChange={(e) => patchStatus(p, e.target.value)}
                      className={`border rounded-full px-3 py-1.5 text-[11px] font-jbmono uppercase tracking-wider bg-transparent focus:outline-none cursor-pointer ${STATUS_STYLE[p.statut] || STATUS_STYLE["À contacter"]}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#161622] text-zinc-200 normal-case">
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{p.dernier_contact || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`prospect-email-${p.id}`}
                        onClick={() => setDraft(p)}
                        title="Générer un email de prospection"
                        className="p-2 text-zinc-500 hover:text-amber-400 transition-colors"
                      >
                        <Mail size={15} />
                      </button>
                      <button
                        data-testid={`prospect-edit-${p.id}`}
                        onClick={() => setModal({ mode: "edit", data: { ...p } })}
                        title="Modifier"
                        className="p-2 text-zinc-500 hover:text-amber-400 transition-colors"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        data-testid={`prospect-delete-${p.id}`}
                        onClick={() => remove(p.id)}
                        title="Supprimer"
                        className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {modal && (
          <motion.div
            data-testid="prospect-form-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-amber-500/20 bg-[#161622] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display font-bold uppercase tracking-wide">
                  {modal.mode === "create" ? "Nouvelle collectivité" : "Modifier la fiche"}
                </h3>
                <button data-testid="prospect-modal-close" onClick={() => setModal(null)} className="text-zinc-500 hover:text-amber-400">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Nom *</label>
                  <input
                    data-testid="prospect-nom-input"
                    required
                    value={modal.data.nom}
                    onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, nom: e.target.value } }))}
                    placeholder="Mairie de…"
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Type</label>
                    <select
                      data-testid="prospect-type-select"
                      value={modal.data.type}
                      onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, type: e.target.value } }))}
                      className={inputCls}
                    >
                      {TYPES.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Département</label>
                    <input
                      data-testid="prospect-departement-input"
                      value={modal.data.departement}
                      onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, departement: e.target.value } }))}
                      placeholder="38 — Isère"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Email</label>
                    <input
                      data-testid="prospect-email-input"
                      value={modal.data.email}
                      onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, email: e.target.value } }))}
                      placeholder="animations@mairie.fr"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Téléphone</label>
                    <input
                      data-testid="prospect-telephone-input"
                      value={modal.data.telephone}
                      onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, telephone: e.target.value } }))}
                      placeholder="04 00 00 00 00"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Statut</label>
                    <select
                      data-testid="prospect-statut-select"
                      value={modal.data.statut}
                      onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, statut: e.target.value } }))}
                      className={inputCls}
                    >
                      {STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Dernier contact</label>
                    <input
                      data-testid="prospect-dernier-contact-input"
                      type="date"
                      value={modal.data.dernier_contact}
                      onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, dernier_contact: e.target.value } }))}
                      className={`${inputCls} [color-scheme:dark]`}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Site web</label>
                  <input
                    data-testid="prospect-site-input"
                    value={modal.data.site_web}
                    onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, site_web: e.target.value } }))}
                    placeholder="https://…"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Notes</label>
                  <textarea
                    data-testid="prospect-notes-input"
                    rows={3}
                    value={modal.data.notes}
                    onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, notes: e.target.value } }))}
                    placeholder="Contact, contexte, relances prévues…"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <button
                  data-testid="prospect-form-submit-button"
                  type="submit"
                  className="w-full bg-amber-500 hover:bg-amber-400 text-white font-bold uppercase tracking-wider text-sm rounded-xl px-6 py-3.5 transition-colors duration-300"
                >
                  {modal.mode === "create" ? "Ajouter la collectivité" : "Enregistrer"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}

        {draft && (
          <motion.div
            data-testid="prospect-email-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setDraft(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-amber-500/20 bg-[#161622] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold uppercase tracking-wide">Email de prospection</h3>
                <button data-testid="email-modal-close" onClick={() => setDraft(null)} className="text-zinc-500 hover:text-amber-400">
                  <X size={20} />
                </button>
              </div>
              <p className="text-xs text-zinc-500 mb-4">
                Modèle personnalisé pour <span className="text-amber-400">{draft.nom}</span>. Copiez-le puis envoyez-le depuis votre messagerie habituelle.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <button
                  data-testid="generate-ai-email-button"
                  onClick={generateAi}
                  disabled={aiLoading}
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 border border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-white disabled:opacity-60 transition-colors duration-300"
                >
                  {aiLoading && <Loader2 size={13} className="animate-spin" />}
                  {aiLoading ? "Génération en cours…" : "Générer une version IA"}
                </button>
                {aiDraft && aiDraft.for === draft.id && (
                  <>
                    <span className="text-[10px] font-jbmono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-2.5 py-0.5">
                      Version IA affichée
                    </span>
                    <button
                      data-testid="standard-email-button"
                      onClick={() => setAiDraft(null)}
                      className="text-xs font-jbmono uppercase tracking-wider text-zinc-500 hover:text-amber-400 transition-colors"
                    >
                      Revenir au modèle standard
                    </button>
                  </>
                )}
              </div>
              <div className="rounded-xl border border-zinc-800 bg-[#09090D] p-5">
                <p className="text-xs font-jbmono uppercase tracking-widest text-zinc-500 mb-2">Objet</p>
                <p data-testid="email-draft-subject" className="text-sm font-semibold text-zinc-100 mb-4">
                  {activeDraft.subject || "—"}
                </p>
                <p className="text-xs font-jbmono uppercase tracking-widest text-zinc-500 mb-2">Message</p>
                <pre data-testid="email-draft-body" className="text-sm text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed">
                  {activeDraft.body}
                </pre>
              </div>
              <button
                data-testid="copy-email-button"
                onClick={copyDraft}
                className="mt-5 w-full flex items-center justify-center gap-2.5 bg-amber-500 hover:bg-amber-400 text-white font-bold uppercase tracking-wider text-sm rounded-xl px-6 py-3.5 transition-colors duration-300"
              >
                {copied ? <Check size={16} /> : <Copy size={15} />}
                {copied ? "Copié !" : "Copier l'email"}
              </button>
              <button
                data-testid="send-email-button"
                onClick={sendEmail}
                disabled={sendLoading || !draft.email}
                className="mt-3 w-full flex items-center justify-center gap-2.5 border border-emerald-500/40 hover:bg-emerald-500 text-emerald-400 hover:text-white disabled:opacity-50 font-bold uppercase tracking-wider text-sm rounded-xl px-6 py-3.5 transition-colors duration-300"
              >
                {sendLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                {sendLoading
                  ? "Envoi en cours…"
                  : draft.email
                    ? `Envoyer à ${draft.email}`
                    : "Aucune adresse email"}
              </button>
              <p className="mt-2 text-[11px] text-zinc-500 text-center">
                Envoie la version affichée, avec la fiche technique en lien de téléchargement.
              </p>
            </motion.div>
          </motion.div>
        )}

        {importOpen && (
          <motion.div
            data-testid="import-csv-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setImportOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-md rounded-2xl border border-amber-500/20 bg-[#161622] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold uppercase tracking-wide">Importer un CSV</h3>
                <button
                  data-testid="import-modal-close"
                  onClick={() => setImportOpen(false)}
                  className="text-zinc-500 hover:text-amber-400"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                Colonnes reconnues : Nom, Type, Département, Email, Téléphone, Site web, Statut,
                Dernier contact, Notes. Le séparateur (« ; » ou « , ») est détecté automatiquement.
                Les doublons (email ou nom déjà présent) et les lignes sans nom sont ignorés.
              </p>
              <input
                data-testid="import-file-input"
                type="file"
                accept=".csv,.txt,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-zinc-300 file:mr-4 file:rounded-full file:border-0 file:bg-amber-500 file:px-4 file:py-2 file:text-xs file:font-bold file:uppercase file:text-white hover:file:bg-amber-400 file:cursor-pointer"
              />
              <button
                data-testid="import-submit-button"
                onClick={runImport}
                disabled={!importFile || importing}
                className="mt-5 w-full flex items-center justify-center gap-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm rounded-xl px-6 py-3.5 transition-colors duration-300"
              >
                {importing && <Loader2 size={16} className="animate-spin" />}
                {importing ? "Import en cours…" : "Importer"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
